import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Syncs i18next language with Firestore `clientes/{uid}.preferredLanguage`.
 * - On mount: reads Firestore preference and applies it.
 * - changeLanguage(): updates i18next, localStorage, and Firestore.
 */
export function useLanguageSync() {
  const { i18n } = useTranslation();

  // On mount: sync from Firestore → i18next
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    let cancelled = false;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clientes', user.uid));
        if (cancelled) return;
        const lang = snap.data()?.preferredLanguage;
        if (lang && ['es', 'en', 'pt'].includes(lang) && lang !== i18n.language) {
          i18n.changeLanguage(lang);
          localStorage.setItem('i18nextLng', lang);
        }
      } catch (err) {
        console.warn('useLanguageSync: could not read preferredLanguage', err);
      }
    })();

    return () => { cancelled = true; };
  }, [i18n]);

  // Callable: update language everywhere
  const changeLanguage = useCallback(async (lng) => {
    if (!['es', 'en', 'pt'].includes(lng)) return;

    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);

    const user = auth.currentUser;
    if (user) {
      try {
        await updateDoc(doc(db, 'clientes', user.uid), { preferredLanguage: lng });
      } catch (err) {
        console.warn('useLanguageSync: could not save preferredLanguage', err);
      }
    }
  }, [i18n]);

  return { currentLanguage: i18n.language, changeLanguage };
}
